import axios from '../../../http';
import formatModel from './format-model';

function initialState() {
  return {
    evCompare: true,
    evOnly: false,
    ashp: true,
    solar: true,
    skipHome: false,
    postcode: '',
    address: {},
    compareFuel: null,
    sameSupplier: null,
    electricitySmartMeter: false,
    gasSmartMeter: false,
    isEconomy7: false,
    isElectricityMorethanGas: false,
    tariff: {},
    tariffValidToDate: null,
    supplierId: null,
    currentPayMethod: 'monthlyDirectDebit',
    utilityPeriod: '',
    currentCostEstimated: null,
    currentUsageEstimated: null,
    gasUsage: { usage: null, cost: null, usageType: 1, usageUnit: 1 },
    electricityUsage: { usage: null, cost: null, usageType: 1, usageUnit: 1 },
    electricityTariffValidToDate: null,
    electricityConsumptionExceedsGasConsumption: false,
    electricitySupplierId: null,
    electricityTariff: {},
    electricityCurrentPayMethod: 'monthlyDirectDebit',
    economy7Percentage: 42,
    gasTariffValidToDate: null,
    gasSupplierId: null,
    gasTariff: {},
    gasCurrentPayMethod: 'monthlyDirectDebit',
    newTariffPaymentMethod: 'monthlyDirectDebit',
    gdprConsent: null,
    propertyId: null,
    propertyUsage: 4,
    electricVehicleOwnership: null,
    iceVehicle: {
      registrationNumber: '',
      makeId: null,
      modelId: null
    },
    electricVehicle: {
      registrationNumber: '',
      makeId: null,
      modelId: null
    },
    vehicleUsage: {
      weekday: 0,
      weekend: 0
    },
    electricVehicleChargeType: null,
    insulation: null,
    heatingFuel: null,
    numberOfAdults: 2,
    numberOfChildren: 2
  };
}

const state = initialState();

const getters = {
  quote: (state) => state,
  quoteType: (state) => {
    let value;

    switch (state.compareFuel) {
      case 'gas':
        value = 'gasToGas';
        break;

      case 'electricity':
        value = 'electricityToElectricity';
        break;

      case 'gasElectricity':
        value =
          state.sameSupplier === 'no'
            ? 'dualDistinctToDualConsolidated'
            : 'dualConsolidatedToDualConsolidated';
        break;

      default:
        value = 'gasToGas';
    }

    return value;
  },
  is02Meter: (state) => {
    const address = state.address.parts;

    if (state.compareFuel == 'gas') return false;
    if (address) {
      const meter = address.elecdetails.find(({ isDomestic }) => isDomestic);

      if (meter) {
        return meter.mpan.substring(0, 2) === '02';
      }

      return state.isEconomy7;
    }

    return state.isEconomy7;
  },
  noMpan: (state) => {
    const address = state.address.parts;

    if (state.compareFuel == 'gas') return false;
    if (address) {
      const elecdetails = address.elecdetails.find(
        ({ isDomestic }) => isDomestic
      );

      return elecdetails ? !elecdetails.mpan : true;
    }

    return false;
  },
  notPrePaymentMeter: (state) =>
    !(
      state.currentPayMethod === 'prePaymentMeter' ||
      state.electricityCurrentPayMethod === 'prePaymentMeter' ||
      state.gasCurrentPayMethod === 'prePaymentMeter'
    )
};

const actions = {
  initialiseState({ commit }) {
    const storage = localStorage.getItem('quote');

    if (storage) {
      commit('updateState', { prop: 'quote', value: JSON.parse(storage) });
    }

    return {};
  },
  setQuoteType({ commit }, quoteType) {
    let compareFuel;
    let sameSupplier;

    switch (quoteType) {
      case 'gasToGas':
        compareFuel = 'gas';
        break;

      case 'electricityToElectricity':
        compareFuel = 'electricity';
        break;

      case 'dualDistinctToDualConsolidated':
        sameSupplier = 'no';
        break;

      case 'dualConsolidatedToDualConsolidated':
        sameSupplier = 'yes';
        break;

      default:
        compareFuel = 'gas';
    }

    commit('updateState', { prop: 'compareFuel', value: compareFuel });
    commit('updateState', { prop: 'sameSupplier', value: sameSupplier });
  },
  async newQuote({ state, rootState, rootGetters, dispatch }, type) {
    try {
      const switchType = rootGetters['quote/quoteType'];
      const model = formatModel(
        state,
        type,
        rootState.session.quote_journey_session
      );
      const session = {
        quoteType: type,
        address: state.address,
        switchType,
        heatFuel: state.heatingFuel
      };

      dispatch(
        'session/setItem',
        { key: 'quote', value: session },
        { root: true }
      );
      dispatch('page_loader/start', 0, { root: true });
      dispatch('page_loader/quoteLoader', null, { root: true });

      const res = await axios({
        method: 'POST',
        url: `/request/customer/esa/${type}/quote`,
        data: model
      });

      if (!res.data.quoteId) throw new Error('Quote could not be created.');

      await dispatch(
        'session/setItem',
        {
          key: 'quote',
          value: { ...session, switchId: res.data.quoteId }
        },
        { root: true }
      );
    } catch (error) {
      console.log(error);
      dispatch('page_loader/stop', null, { root: true });
      return error.message || 'Something went wrong.';
    }
  },
  async editQuote({ rootState, dispatch }, data) {
    const { switchId, quoteType } = rootState.session.quote;
    dispatch('page_loader/start', 0, { root: true });
    dispatch('page_loader/quoteLoader', null, { root: true });

    try {
      const res = await axios({
        method: 'PUT',
        url: `/request/customer/esa/${quoteType}/quote`,
        data: {
          ...data,
          quoteId: switchId,
          referrerBranch: process.env.VUE_APP_REFERRER_BRANCH_CODE
        }
      });

      return res.data;
    } catch (error) {
      console.log(error);
      dispatch('page_loader/start', 0, { root: true });

      return error;
    }
  },
  async getDefaults({ state, rootGetters, dispatch }, data) {
    try {
      const res = await axios({
        method: 'POST',
        url: '/request/Address/recco/details/default',
        data
      });

      return res.data;
    } catch (error) {
      console.log(error);
      return {};
    }
  }
};

const mutations = {
  updateState(state, { prop, value }) {
    state[prop] = value;
  },
  resetState(state) {
    Object.assign(state, initialState());
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
